import type { FrameworkPackDefinition } from "@/lib/behavior/frameworkPacks/frameworkPackTypes";

export const ADAPTIVE_LEADERSHIP_PACK: FrameworkPackDefinition = {
  id: "adaptive_leadership",
  label: "Adaptive Leadership",
  family: "veera_core",
  source: "curated",
  preview: {
    philosophySummary:
      "Adaptive Leadership notices the difference between technical fixes and deeper adaptive work involving values, uncertainty, and shared responsibility.",
    whatItNotices: [
      "Where a recurring tension may not be solvable by expertise or process alone.",
      "Which stakeholders, values, or loyalties appear to be in tension.",
      "Where the group may be avoiding productive disequilibrium or over-relying on authority.",
    ],
    watchouts: [
      "Do not overcomplicate a straightforward execution issue.",
      "Do not blame the system when individual behavior is clearly observable.",
      "Do not infer politics or motives beyond the available evidence.",
    ],
    sampleCoachLanguage: [
      "What part of this is technical, and what part may be adaptive?",
      "Which values or stakeholders are in tension here?",
    ],
    sampleLeaderLanguage: [
      "Through this lens, the moment may point to work the system needs to face, not just a problem for one person to solve.",
      "A useful reflection is whether the group is treating an adaptive tension like a technical task.",
    ],
  },
  runtime: {
    coreConstructs: ["Adaptive challenge", "Technical problem", "Disequilibrium", "Values tension", "Authority dependency"],
    diagnosticQuestions: [
      "What part of the challenge can be solved technically, and what part requires learning?",
      "Which values or loyalties are competing in this moment?",
      "Where is responsibility being returned to the leader instead of held by the system?",
    ],
    interventionPatterns: [
      "Distinguish technical from adaptive work.",
      "Name values in tension.",
      "Notice avoidance of productive heat without escalating it unnecessarily.",
    ],
    progressMarkers: [
      "The leader distinguishes execution issues from learning challenges.",
      "The system's shared work is named without blame.",
      "Tension can be held long enough for learning to occur.",
    ],
    stallSignals: [
      "The leader keeps applying technical fixes to recurring adaptive tensions.",
      "The group avoids discomfort so quickly that learning cannot happen.",
    ],
  },
  operationalization: {
    noticingSignals: [
      {
        id: "technical_fix_for_adaptive_tension",
        label: "Technical fix for adaptive tension",
        description: "A recurring or values-laden issue is treated mainly as a task, process, or expertise problem.",
        positiveIndicators: ["process", "decision", "alignment", "recurring", "stakeholders"],
        negativeIndicators: ["quick fix", "move on", "just", "simple"],
        evidenceHints: ["same issue again", "alignment but tension", "process fix", "competing priorities"],
        prohibitedInferences: ["political avoidance", "lack of courage", "system dysfunction"],
      },
      {
        id: "disequilibrium_avoidance",
        label: "Disequilibrium avoidance",
        description: "Productive tension appeared but was reduced before learning could happen.",
        positiveIndicators: ["tension", "uncertainty", "disagreement", "heat"],
        negativeIndicators: ["smooth over", "move on", "defer", "avoid"],
        evidenceHints: ["became tense", "moved past", "did not explore", "kept harmony"],
        prohibitedInferences: ["fear", "avoidant personality", "political motive"],
      },
    ],
    reflectionPatterns: [
      {
        id: "adaptive_vs_technical",
        label: "Adaptive versus technical distinction",
        promptTemplates: [
          "What part of this moment looked technical, and what part may have required learning?",
          "Which values or stakeholders may have been in tension here?",
          "What work might belong to the group rather than only to the leader?",
        ],
        useWhen: ["technical_fix_for_adaptive_tension", "disequilibrium_avoidance"],
        avoidWhen: ["simple_execution_issue", "thin_evidence"],
      },
    ],
    boundaries: {
      allowed: ["adaptive tension", "technical work", "values tension", "shared work", "productive disequilibrium"],
      prohibited: ["the system is broken", "political avoidance", "fear of heat", "you should escalate"],
      coachOnly: ["diagnose adaptive challenge", "manage heat", "design systemic intervention"],
    },
    salienceWeights: {
      technical_fix_for_adaptive_tension: 1.4,
      disequilibrium_avoidance: 1.5,
    },
  },
};
